import { useState } from "react";
import { Link } from "react-router-dom";

const planFeatures = {
  free: [
    { icon: "ri-video-line", text: "動画講座 3本まで出品", available: true },
    { icon: "ri-box-3-line", text: "教材キット 3点まで出品", available: true },
    { icon: "ri-add-circle-line", text: "追加動画枠（月額1,500円/本）", available: true },
    { icon: "ri-percent-line", text: "動画販売手数料 20%", available: true },
    { icon: "ri-percent-line", text: "教材手数料 15%", available: true },
    { icon: "ri-bar-chart-2-line", text: "簡易分析（購入数・閲覧数）", available: true },
    { icon: "ri-line-chart-line", text: "詳細売上・購入者動向分析", available: false },
    { icon: "ri-megaphone-line", text: "特集掲載候補への追加", available: false },
    { icon: "ri-customer-service-line", text: "優先サポート", available: false },
  ],
  light: [
    { icon: "ri-video-line", text: "動画講座 無制限出品", available: true },
    { icon: "ri-box-3-line", text: "教材キット 無制限出品", available: true },
    { icon: "ri-add-circle-line", text: "追加動画枠（不要・無制限）", available: true },
    { icon: "ri-percent-line", text: "動画販売手数料 10%（フリーの半額）", available: true },
    { icon: "ri-percent-line", text: "教材手数料 8%（フリーの半額以下）", available: true },
    { icon: "ri-bar-chart-2-line", text: "簡易分析（購入数・閲覧数）", available: true },
    { icon: "ri-line-chart-line", text: "詳細売上・購入者動向分析", available: true },
    { icon: "ri-megaphone-line", text: "特集掲載候補への追加", available: true },
    { icon: "ri-customer-service-line", text: "優先サポート", available: true },
  ],
};

const feeComparison = [
  { sales: "月10,000円", freeFee: "2,000円", lightFee: "1,000円", lightProfit: "4,000円還元" },
  { sales: "月50,000円", freeFee: "10,000円", lightFee: "5,000円", lightProfit: "0円（±0）" },
  { sales: "月100,000円", freeFee: "20,000円", lightFee: "10,000円", lightProfit: "+5,000円利益" },
  { sales: "月300,000円", freeFee: "60,000円", lightFee: "30,000円", lightProfit: "+25,000円利益" },
];

const additionalPricing = [
  { item: "追加動画枠（フリープランのみ）", price: "1,500円/本・月", note: "本数が増える場合はライトプランがお得" },
];

export default function PricingPage() {
  const [billingCycle] = useState<"monthly">("monthly");

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-deep-green py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/f3a696bf853b13375920def5cfbb7ab9.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-screen-lg mx-auto text-center">
          <p className="text-gold text-xs font-display italic tracking-widest mb-4">Pricing Plans</p>
          <h1 className="font-serif text-white text-4xl md:text-5xl font-light mb-4">料金プラン</h1>
          <p className="text-white/70 font-sans text-sm leading-relaxed max-w-lg mx-auto">
            TSUGITEはまず無料で始められます。売上が安定してきたらライトプランへの移行がお得になります。
          </p>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="py-16 px-4 bg-ivory">
        <div className="max-w-screen-md mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* Free Plan */}
            <div className="bg-white rounded-3xl p-8 border-2 border-stone-200">
              <div className="mb-6">
                <p className="text-xs text-charcoal/50 font-sans uppercase tracking-widest mb-1">Free Plan</p>
                <h2 className="font-serif text-deep-green text-2xl mb-2">フリープラン</h2>
                <div className="flex items-end gap-1 mb-2">
                  <span className="font-serif text-4xl text-charcoal">¥0</span>
                  <span className="text-charcoal/50 font-sans text-sm mb-1">/月</span>
                </div>
                <p className="text-xs text-charcoal/50 font-sans">まず試してみたい方に</p>
              </div>
              <ul className="space-y-3 mb-8">
                {planFeatures.free.map((f, i) => (
                  <li key={i} className={`flex items-center gap-3 text-sm font-sans ${f.available ? "text-charcoal/80" : "text-charcoal/30"}`}>
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0`}>
                      <i className={`${f.available ? "ri-checkbox-circle-line text-teal" : "ri-close-circle-line text-stone-200"} text-lg`} />
                    </div>
                    {f.text}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="block w-full py-3 rounded-full border-2 border-deep-green text-deep-green text-sm font-sans text-center cursor-pointer hover:bg-deep-green hover:text-white transition-colors whitespace-nowrap">
                無料で始める
              </Link>
            </div>

            {/* Light Plan */}
            <div className="bg-deep-green rounded-3xl p-8 border-2 border-deep-green relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gold text-white text-xs px-4 py-1 rounded-full font-sans whitespace-nowrap">おすすめ</span>
              </div>
              <div className="mb-6">
                <p className="text-xs text-gold/70 font-sans uppercase tracking-widest mb-1">Light Plan</p>
                <h2 className="font-serif text-white text-2xl mb-2">ライトプラン</h2>
                <div className="flex items-end gap-1 mb-2">
                  <span className="font-serif text-4xl text-white">¥5,000</span>
                  <span className="text-white/50 font-sans text-sm mb-1">/月</span>
                </div>
                <p className="text-xs text-white/50 font-sans">本格的に販売したい方に</p>
              </div>
              <ul className="space-y-3 mb-8">
                {planFeatures.light.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-sans text-white/85">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-gold text-lg" />
                    </div>
                    {f.text}
                  </li>
                ))}
              </ul>
              <button className="block w-full py-3 rounded-full bg-gold text-white text-sm font-sans text-center cursor-pointer hover:bg-amber-500 transition-colors whitespace-nowrap">
                ライトプランで始める
              </button>
              <p className="text-xs text-white/40 font-sans text-center mt-3">※ Stripe決済連携後に有効化されます</p>
            </div>
          </div>
        </div>
      </section>

      {/* Fee Comparison Table */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-screen-md mx-auto">
          <div className="mb-10 text-center">
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">Fee Simulation</p>
            <h2 className="font-serif text-deep-green text-2xl font-light">手数料比較シミュレーション</h2>
            <p className="text-charcoal/60 font-sans text-sm mt-2">月間売上ごとのざっくり比較です（教材は除く）</p>
          </div>
          <div className="bg-ivory rounded-3xl overflow-hidden border border-stone-100">
            <div className="grid grid-cols-4 bg-deep-green/5 border-b border-stone-100">
              <div className="p-4"><p className="text-xs font-sans text-charcoal/50">月間売上</p></div>
              <div className="p-4"><p className="text-xs font-sans text-charcoal/50">フリープラン手数料</p></div>
              <div className="p-4"><p className="text-xs font-sans text-charcoal/50">ライトプラン手数料</p></div>
              <div className="p-4"><p className="text-xs font-sans text-charcoal/50">ライトの損得</p></div>
            </div>
            {feeComparison.map((row, i) => (
              <div key={i} className={`grid grid-cols-4 border-b border-stone-100 ${i === feeComparison.length - 1 ? "border-b-0" : ""}`}>
                <div className="p-4"><p className="text-sm font-sans text-charcoal font-medium">{row.sales}</p></div>
                <div className="p-4"><p className="text-sm font-sans text-charcoal/60">{row.freeFee}</p></div>
                <div className="p-4"><p className="text-sm font-sans text-teal font-medium">{row.lightFee}</p></div>
                <div className="p-4">
                  <p className={`text-xs font-sans font-medium ${row.lightProfit.includes("+") ? "text-teal" : row.lightProfit.includes("±") ? "text-charcoal/50" : "text-amber-500"}`}>
                    {row.lightProfit}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-charcoal/40 font-sans mt-3 text-center">
            ※ライトプランは月額5,000円を含んだ計算です。月間売上50,000円以上でライトプランが有利になります。
          </p>
        </div>
      </section>

      {/* Additional Options */}
      <section className="py-12 px-4 bg-ivory">
        <div className="max-w-screen-md mx-auto">
          <div className="mb-8 text-center">
            <h2 className="font-serif text-deep-green text-2xl font-light">追加オプション</h2>
          </div>
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
            <div className="grid grid-cols-3 bg-stone-50 border-b border-stone-100 px-6 py-3">
              {["オプション内容", "料金", "備考"].map((h) => (
                <p key={h} className="text-xs font-sans text-charcoal/50">{h}</p>
              ))}
            </div>
            {additionalPricing.map((item, i) => (
              <div key={i} className="grid grid-cols-3 px-6 py-4 border-b border-stone-50 last:border-b-0">
                <p className="text-sm font-sans text-charcoal">{item.item}</p>
                <p className="text-sm font-sans text-teal font-medium">{item.price}</p>
                <p className="text-xs font-sans text-charcoal/50">{item.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Handcraft Note */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-screen-md mx-auto">
          <div className="bg-ivory rounded-3xl p-8 border border-stone-100">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal/10 flex-shrink-0">
                <i className="ri-information-line text-teal text-xl" />
              </div>
              <div>
                <h3 className="font-serif text-deep-green text-lg mb-3">プランに関するご注意</h3>
                <ul className="space-y-2 text-sm text-charcoal/70 font-sans leading-relaxed">
                  <li className="flex items-start gap-2"><i className="ri-arrow-right-s-line text-teal flex-shrink-0 mt-0.5" />ライトプランの決済はStripe（クレジットカード決済）を利用します。接続後に有効化されます。</li>
                  <li className="flex items-start gap-2"><i className="ri-arrow-right-s-line text-teal flex-shrink-0 mt-0.5" />フリープランからライトプランへはいつでも移行できます。</li>
                  <li className="flex items-start gap-2"><i className="ri-arrow-right-s-line text-teal flex-shrink-0 mt-0.5" />ライトプランのキャンセルは次の更新日前に申請いただくと、翌月から適用されます。</li>
                  <li className="flex items-start gap-2"><i className="ri-arrow-right-s-line text-teal flex-shrink-0 mt-0.5" />販売手数料はご購入が成立した際に自動的に差し引かれます。</li>
                  <li className="flex items-start gap-2"><i className="ri-arrow-right-s-line text-teal flex-shrink-0 mt-0.5" />消費者（購入者）の利用は常に無料です。</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-ivory">
        <div className="max-w-screen-sm mx-auto text-center">
          <p className="text-gold text-xs font-display italic tracking-widest mb-3">Get Started</p>
          <h2 className="font-serif text-deep-green text-2xl font-light mb-4">まずは無料で始めてみましょう</h2>
          <p className="text-charcoal/60 text-sm font-sans leading-relaxed mb-7">
            初期費用・月額料金は一切かかりません。<br />出品ガイドを読んで、今日から技術を届けてください。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="bg-deep-green text-white px-8 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap inline-block">
              無料で登録する →
            </Link>
            <Link to="/guide" className="border border-deep-green/30 text-deep-green px-8 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-white transition-colors whitespace-nowrap inline-block">
              出品ガイドを見る
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
